

import React, { useState } from 'react';
import './FAQ.css';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqData: FAQItem[] = [
  {
    question: 'What is PRO Market History?',
    answer: <>
      PRO Market History is a community-driven tool for tracking Pokémon and item auction/trade data in Pokémon Revolution Online (PRO). It helps players make more informed trading decisions by providing verified historical sales data.<br/><br/>
      <i>Note: This is a community project and is not affiliated with the official Pokémon Revolution Online team.</i>
    </>
  },
  {
    question: 'Does this web-app give me an “average price”?',
    answer: <>No. We do not provide an "average price.” Prices in PRO are highly variable and context-dependent. Instead, we showcase real, verified sales history so you can draw your own conclusions.</>
  },
  {
    question: 'How does the app work?',
    answer: <>
      <ul>
        <li>Use the <b>Browse Data</b> tab to filter and search for Pokémon or items.</li>
        <li>If enough data exists, you’ll also see a <b>graph of sales over time</b>.</li>
        <li>Registered users can <b>submit data with proof</b>.</li>
        <li>Submissions are reviewed by a <b>reviewer</b> before being added to the database.</li>
      </ul>
    </>
  },
  {
    question: 'How do I submit price data?',
    answer: <>
      <ol>
        <li>Go to <b>Submit Data</b>.</li>
        <li>Select <b>Pokémon</b> or <b>Item</b>.</li>
        <li>Fill in the required info (price, date sold, details).</li>
        <li>Upload proof (e.g., forum auction link).</li>
        <li>A reviewer will check your submission and either approve or reject it.</li>
      </ol>
    </>
  },
  {
    question: 'What kind of proof is required?',
    answer: <>
      We accept:<br/>
      <ul>
        <li>Forum Auctions</li>
      </ul>
    </>
  },
  {
    question: 'Why do I need to provide Pokémon IVs?',
    answer: <>
      IVs greatly affect a Pokémon’s value. All <b>6 IVs (HP, Atk, Def, SpAtk, SpDef, Speed)</b> are required.<br/>
      The system automatically calculates <b>Hidden Power</b> from your inputs.
    </>
  },
  {
    question: 'Can I edit my submission after sending it?',
    answer: <>
      No. Once submitted, you cannot edit the entry.<br/>
      <ul>
        <li>Reviewers can adjust data during the review process.</li>
        <li>After approval, only admins can change data.</li>
      </ul>
    </>
  },
  {
    question: 'Why was my submission rejected?',
    answer: <>
      Possible reasons include:<br/>
      <ul>
        <li>Insufficient or unclear proof</li>
        <li>Incorrect details (price, IVs, nature, etc.)</li>
        <li>Duplicate entries</li>
        <li>Data not matching the provided proof</li>
      </ul>
    </>
  },
  {
    question: 'How are prices displayed?',
    answer: <>
      <ul>
        <li>All prices are shown in <b>Pokédollars (PD)</b>.</li>
        <li>Entries are based on <b>user submissions + reviewer approval</b>.</li>
        <li>The system allows filtering by <b>Pokémon, stats, nature, ability, and more</b>.</li>
        <li>Graphs display sales trends over time when enough data exists.</li>
      </ul>
    </>
  },
  {
    question: 'Can I search for specific Pokémon or items?',
    answer: <>Yes. Use the <b>Data Showcase</b> search and filters (Pokémon, items, price ranges, date ranges, stats, etc.). You can also sort results by price or date.</>
  },
  {
    question: 'How often is the database updated?',
    answer: <>In <b>real time</b>. Once a submission is approved, it appears immediately.</>
  },
  {
    question: 'What if I find incorrect price data?',
    answer: <>Report it via <b>Discord</b> to an admin with as much detail as possible. We’ll investigate and fix it if necessary.</>
  },
  {
    question: 'Do I need an account?',
    answer: <>Yes. To submit data you must register and log in. Browsing the data is available to everyone.</>
  },
  {
    question: 'Will my data be shared?',
    answer: <>No. Only a <b>username, email, and password</b> are required. Your personal data will not be shared.</>
  },
  {
    question: 'What is the Admin Panel for?',
    answer: <>The Admin Panel is for internal use only (managing users, reviewing data, removing duplicates, etc.).</>
  },
  {
    question: 'How do I become a Reviewer?',
    answer: <>Reviewers are selected by the admins based on <b>community involvement, trustworthiness, and knowledge of PRO market values</b>. If you’re interested, contact me on Discord.</>
  },
  {
    question: 'Do rejected submissions get stored?',
    answer: <>Yes. They are stored but hidden from all regular users.</>
  },
  {
    question: 'What happens if someone tries to fake data?',
    answer: <>All submissions are reviewed. If a reviewer is found approving <b>faulty submissions en masse</b> to manipulate data, the user will be banned and all their approved submissions will be sent back into the review process.</>
  },
];

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div className="faq-container">
      <header className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>PRO Market History – FAQ</h1>
            <p>Here you’ll find answers to the most common questions about the project, how to use it, and how data is managed.</p>
          </div>
        </div>
      </header>
      <main className="faq-main">
        <div className="faq-content">
          <div className="faq-list">
            {faqData.map((item, index) => (
              <div key={index} className={`faq-item ${openItems.includes(index) ? 'open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => toggleItem(index)}
                >
                  <span className="question-text">{item.question}</span>
                  <span className="toggle-icon">
                    {openItems.includes(index) ? '−' : '+'}
                  </span>
                </button>
                <div className="faq-answer">
                  <div className="answer-content">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="faq-footer">
            <h3>Still have questions?</h3>
            <p>If you couldn't find the answer you were looking for, please reach out us on our <a href="https://discord.gg/kteFmyZEDY" target="_blank" rel="noopener noreferrer">Discord Server</a></p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQ;
